import type { AppMessages } from '../../types';

export const app: AppMessages = {
  common: {
    loading: 'Laden...',
    saving: 'Opslaan...',
    save: 'Opslaan',
    back: 'Terug',
    error: 'Fout',
    notSet: 'Niet ingesteld',
    user: 'Gebruiker',
    system: 'Systeem',
    copied: 'Gekopieerd!',
    copy: 'Kopiëren',
    cancel: 'Annuleren',
    creating: 'Aanmaken...',
    processing: 'Verwerken...',
    you: 'Jij',
    stars: 'Stars',
    seeAll: 'Alles bekijken',
    filter: 'Filteren',
    amount: 'Bedrag',
    date: 'Datum',
    details: 'Details',
    mustBeLoggedIn: 'Je moet ingelogd zijn',
    daysShort: 'd',
    hoursShort: 'u',
    minutesShort: 'min',
    justNow: 'Zojuist',
    noTitle: 'Geen titel',
    payment: 'betaling',
    payments: 'betalingen',
  },
  layout: {
    dashboard: 'Dashboard',
    invoices: 'Facturen',
    ledger: 'Grootboek',
    withdraws: 'Opnames',
    redirects: 'Doorverwijzingen',
    settings: 'Instellingen',
    admin: 'Admin',
    profile: 'Profiel',
    logout: 'Uitloggen',
    tonConfigured: 'TON geconfigureerd',
    configureTon: 'TON configureren',
    tonNotConfigured: 'TON-adres niet geconfigureerd',
    active: 'Actief',
    walletConnect: 'Wallet koppelen',
    walletTitle: 'Koppel je TON-wallet',
    walletSubtitle: 'Voer het TON-adres in dat je opnames zal ontvangen.',
    walletConnectedTitle: 'Wallet gekoppeld',
    walletConnectedSubtitle: 'Je opnames worden naar dit adres gestuurd.',
    walletCurrentAddress: 'Gekoppeld adres',
    walletAddressLabel: 'TON-adres',
    walletHint: 'User-friendly (EQ… / UQ…) of raw (0:…) formaat toegestaan.',
    walletInvalidAddress: 'Ongeldig TON-adres',
    walletUpdate: 'Adres bijwerken',
    walletChangeAddress: 'Adres wijzigen',
    walletDisconnect: 'Ontkoppelen',
    walletDisconnectConfirmTitle: 'Wallet ontkoppelen?',
    walletDisconnectConfirmBody: 'Je kunt geen opnames aanvragen totdat je een nieuw adres koppelt.',
    walletSaved: 'Wallet succesvol gekoppeld',
    walletDisconnected: 'Wallet ontkoppeld',
    newPaymentLink: 'Nieuwe betaallink',
    openBot: 'Telegram-bot',
    helpCenter: 'Helpcentrum',
  },
  login: {
    seoTitle: 'Inloggen | Telegram Stars betalingsplatform',
    seoDescription: 'Log in op je Telegram Stars Payment Platform account',
    signIn: 'Inloggen',
    signUp: 'Registreren',
    email: 'E-mail',
    password: 'Wachtwoord',
    displayNameOptional: 'Weergavenaam (optioneel)',
    displayNamePlaceholder: 'Je naam',
    emailPlaceholder: 'jouw@email.com',
    signInButton: 'Inloggen',
    signUpButton: 'Registreren',
    signInWithGoogle: 'Inloggen met Google',
    or: 'OF',
    welcome: 'Welkom',
    subtitle: 'Log in of maak een account aan om te beginnen',
    minPasswordHint: 'Minimaal 6 tekens',
    tipTelegram: '💡 Tip: je kunt je Telegram-account koppelen in instellingen na registratie',
    continueWithTelegram: 'Doorgaan met Telegram',
    telegramAuthError: 'Inloggen met Telegram mislukt. Probeer opnieuw.',
    featureInstant: 'Direct account aanmaken',
    featureManage: 'Beheer je Telegram Stars betalingen',
    featureWithdraw: 'Neem je saldo op in TON',
    emailRequired: 'Voer je e-mailadres in',
    emailInvalid: 'Ongeldig e-mailformaat',
    passwordRequired: 'Voer je wachtwoord in',
    passwordRequiredSignup: 'Voer een wachtwoord in',
    passwordMinLength: 'Wachtwoord moet minimaal 6 tekens bevatten',
    genericError: 'Er is een fout opgetreden',
    recaptchaFailed: 'reCAPTCHA-verificatie mislukt. Probeer het opnieuw.',
    invalidCredentials: 'Onjuist e-mailadres of wachtwoord. Als je nog geen account hebt, maak er een aan in het tabblad «Registreren».',
    wrongPassword: 'Onjuist wachtwoord. Controleer je wachtwoord.',
    tooManyAttempts: 'Te veel pogingen. Probeer het later opnieuw.',
    accountDisabled: 'Dit account is uitgeschakeld. Neem contact op met support.',
    authNotEnabled: 'E-mail/wachtwoord-authenticatie is niet ingeschakeld. Controleer de Firebase-configuratie.',
    emailInUse: 'Dit e-mailadres is al in gebruik. Log in met dit adres.',
    googleAuthError: 'Er is een fout opgetreden bij Google-authenticatie',
    popupClosed: 'Popup is gesloten. Probeer het opnieuw.',
    popupBlocked: 'Popup is geblokkeerd. Sta popups toe en probeer het opnieuw.',
    popupCancelled: 'Er is slechts één popupverzoek tegelijk toegestaan.',
  },
  dashboard: {
    loading: 'Dashboard laden...',
    overallSummary: 'Totaaloverzicht',
    lastMonth: 'Vorige maand',
    lastWeek: 'Vorige week',
    lastYear: 'Vorig jaar',
    balance: 'Saldo',
    available: 'Beschikbaar',
    totalEarned: 'Totaal verdiend',
    fromLastMonth: 'ten opzichte van vorige maand',
    recentPayments: 'Recente betalingen',
    last7days: 'Laatste 7 dagen',
    last30days: 'Laatste 30 dagen',
    quickActions: 'Snelle acties',
    createLink: 'Link maken',
    createLinkDesc: 'Genereer een nieuwe betaallink',
    withdraw: 'Opnemen',
    withdrawDesc: 'Neem je Stars op in TON',
    invoices: 'Facturen',
    invoicesDesc: 'Bekijk betalingsgeschiedenis',
    ledger: 'Grootboek',
    ledgerDesc: 'Bekijk alle transacties',
    recentActivity: 'Recente activiteit',
    noRecentActivity: 'Geen recente activiteit',
    mostRecent: 'Meest recent',
    noRecentInvoices: 'Geen recente facturen',
    allInvoices: 'Alle facturen',
    paymentReceived: 'Betaling ontvangen: {title}',
    withdrawalRequested: 'Opname aangevraagd: {amount} {suffix}',
    transaction: 'Transactie: {amount} {suffix}',
    invoiceLabel: 'Factuur #{id}',
    failedLoadBalance: 'Saldo laden mislukt',
    chartAmount: 'Bedrag',
  },
  invoices: {
    loading: 'Laden...',
    allInvoices: 'Alle facturen',
    paid: 'Betaald',
    sent: 'Verzonden',
    created: 'Aangemaakt',
    expired: 'Verlopen',
    filter: 'Filteren',
    createInvoice: '+ Factuur maken',
    noInvoices: 'Geen facturen gevonden',
    relatedTo: 'Gerelateerd aan:',
    paymentMode: '{mode}-betaling',
    copyLink: 'Link kopiëren',
    copyLinkTitle: 'Factuurlink kopiëren',
    linkNotAvailable: 'Factuurlink niet beschikbaar',
    linkCopied: 'Factuurlink gekopieerd naar klembord!',
    copyFailed: 'Link kopiëren mislukt',
    failedLoad: 'Facturen laden mislukt',
    statusPaid: 'betaald',
    statusSent: 'verzonden',
    statusCreated: 'aangemaakt',
    statusExpired: 'verlopen',
    typeSingleUse: 'eenmalig',
    typeRecurring: 'terugkerend',
  },
  createInvoice: {
    back: 'Terug',
    title: 'Betaallink maken',
    subtitle: 'Genereer in seconden een nieuwe betaallink',
    successTitle: 'Betaallink aangemaakt!',
    successSubtitle: 'Je betaallink is klaar om te delen',
    paymentLink: 'Betaallink:',
    integrationTitle: 'Integreer deze link op je website',
    integrationDesc: 'Onze Telegram-bot begeleidt je stap voor stap (API, HTML-knop, doorverwijzingen).',
    openTutorial: 'Tutorial openen',
    testLink: 'Link testen',
    viewInvoices: 'Mijn facturen bekijken',
    createNew: 'Nieuwe link maken',
    shareTelegram: 'Delen op Telegram',
    paymentMode: 'Betaalmodus',
    modeStars: 'Stars',
    modeEur: 'EUR (€)',
    modeUsd: 'USD ($)',
    amountInteger: 'Bedrag (geheel getal)',
    amountDecimal: 'Bedrag (decimaal)',
    amountRequired: 'Voer een bedrag in',
    amountIntegerError: 'Bedrag moet een geheel getal groter dan 0 zijn',
    amountNoDecimals: 'Stars-bedrag moet een geheel getal zijn (geen decimalen)',
    amountMustBeInteger: 'Stars-bedrag moet een geheel getal zijn',
    amountGreaterThanZero: 'Bedrag moet groter zijn dan 0',
    titleOptional: 'Titel (optioneel)',
    titlePlaceholder: 'Betalingstitel (bijv.: Betaling voor diensten)',
    descriptionOptional: 'Beschrijving (optioneel)',
    descriptionPlaceholder: 'Gedetailleerde betalingsbeschrijving...',
    singleUse: 'Eenmalige link',
    singleUseTooltip: 'Indien ingeschakeld, kan deze link slechts één keer worden gebruikt. Na betaling is hij niet meer geldig.',
    createLink: 'Link maken',
    cancel: 'Annuleren',
    invalidAmount: 'Ongeldig bedrag',
    starsMustBeInteger: 'Bedrag moet een geheel getal zijn voor Stars',
    createError: 'Fout bij het maken van factuur',
  },
  ledger: {
    title: 'Grootboek',
    typeColumn: 'Type',
    description: 'Volledige geschiedenis van al je transacties',
    loading: 'Laden...',
    noEntries: 'Momenteel geen transacties.',
    typePayment: 'Betaling',
    typeFee: 'Kosten',
    typeNetPayment: 'Nettobetaling',
    typeWithdrawal: 'Opname',
    typeAdjustment: 'Aanpassing',
    amountStars: 'Bedrag (Stars)',
    invoiceDetail: 'Factuur: {title} ({amount} Stars)',
    paidBy: 'Betaald door: {payer}',
    payerId: '(ID: {id})',
    withdrawDetail: 'Opname: {amount} Stars (Status: {status})',
    failedLoad: 'Grootboek laden mislukt',
  },
  withdraws: {
    title: 'Mijn opnameverzoeken',
    newRequest: '+ Nieuw verzoek',
    totalLedger: 'Totaal grootboek',
    available: 'Beschikbaar',
    pendingWithdrawals: 'Openstaande opnames',
    hold21days: '21 dagen blokkering',
    payoutNote: 'Uitbetalingen worden handmatig verwerkt door een admin. Bij opname geldt een fee van 3%.',
    forecastTitle: 'Opnameprognose',
    loadingForecast: 'Prognose laden...',
    noForecast: 'Geen aankomende opnamedata',
    paymentsCount: '({count} betalingen)',
    noRequests: 'Momenteel geen opnameverzoeken.',
    createFirst: 'Maak je eerste verzoek',
    amount: 'Bedrag:',
    destination: 'Bestemming:',
    processedOn: 'Verwerkt op:',
    externalRef: 'Externe referentie:',
    errorLabel: 'Fout:',
    rejectionReason: 'Reden van afwijzing:',
    failedLoad: 'Opnames laden mislukt',
    loading: 'Laden...',
  },
  createWithdraw: {
    title: 'Opname aanvragen',
    balanceSource: 'Saldo (bron: backend-grootboek)',
    available: 'Beschikbaar: {amount} Stars',
    pending: 'Openstaande opnames: {amount} Stars',
    waiting: '21 dagen blokkering: {amount} Stars',
    minAndFee: 'Minimumopname: {min} Stars · Platformfee: 3% bij uitbetaling (handmatige verwerking)',
    amountLabel: 'Bedrag (Stars)',
    minPlaceholder: 'Minimum: {min}',
    feeHint: 'Bruto: {gross} Stars · Fee (3%): {fee} Stars · Je ontvangt: {net} Stars',
    useRegistered: 'Mijn geregistreerde TON-adres gebruiken',
    registeredAddress: 'Geregistreerd adres:',
    tonDestination: 'TON-bestemmingsadres',
    tonPlaceholder: 'EQAbc123...',
    tonFormatHint: 'Formaat: EQ... of 0:... (base64 of hex)',
    createRequest: 'Verzoek aanmaken',
    cancel: 'Annuleren',
    creating: 'Aanmaken...',
    insufficientBalance: '⚠️ Onvoldoende saldo voor opname (minimum: {min} Stars)',
    amountMustBeInteger: 'Bedrag moet een positief geheel getal zijn',
    createError: 'Fout bij het maken van opnameverzoek',
  },
  redirects: {
    title: 'Doorverwijzingslinks',
    description: 'Maak korte links die doorverwijzen naar een URL naar keuze. Het delen van /r/jouw-slug stuurt bezoekers naar de geconfigureerde URL.',
    newRedirect: 'Nieuwe doorverwijzing',
    clicks: 'klikken',
    unique: 'uniek',
    links: 'links',
    modalTitle: 'Nieuwe doorverwijzing',
    slugLabel: 'Slug (link-ID)',
    slugRequired: 'Verplicht veld',
    slugPattern: 'Alleen letters, cijfers, streepjes en underscores',
    slugMax: 'Max. 64 tekens',
    slugReserved: 'Deze slug is gereserveerd. Kies een andere.',
    slugPlaceholder: 'bijv.: mijn-site',
    urlLabel: 'Bestemmings-URL',
    urlInvalid: 'Ongeldige URL',
    urlHttpOnly: 'Alleen http- en https-URL\'s worden geaccepteerd.',
    urlTooLong: 'URL te lang (max. 2048 tekens).',
    urlPlaceholder: 'https://example.com/page',
    createButton: 'Doorverwijzing maken',
    loading: 'Laden…',
    empty: 'Geen doorverwijzingen. Klik op «Nieuwe doorverwijzing» om er een te maken.',
    copyLink: 'Link kopiëren',
    linkCopied: 'Link gekopieerd',
    deleteTitle: 'Deze doorverwijzing verwijderen?',
    yes: 'Ja',
    no: 'Nee',
    delete: 'Verwijderen',
    totalClicks: 'Totaal klikken',
    uniqueClicks: 'Unieke klikken',
    avgClicks: 'Gem. klikken/bezoeker',
    loadError: 'Fout bij laden doorverwijzingen',
    createSuccess: 'Doorverwijzingslink aangemaakt',
    createError: 'Fout bij maken doorverwijzing',
    deleteSuccess: 'Doorverwijzing verwijderd',
    deleteError: 'Fout bij verwijderen doorverwijzing',
  },
  adminWithdraws: {
    title: 'Administratie - Opnameverzoeken',
    filterByStatus: 'Filteren op status:',
    all: 'Alle',
    noRequests: 'Geen verzoeken voor deze status.',
    agent: 'Agent:',
    amount: 'Bedrag:',
    destination: 'Bestemming:',
    processedOn: 'Verwerkt op:',
    externalRef: 'Ext. ref.:',
    errorLabel: 'Fout:',
    reason: 'Reden:',
    approve: '✓ Goedkeuren',
    reject: '✗ Afwijzen',
    markPaid: '✓ Markeer betaald',
    markFailed: '✗ Markeer mislukt',
    approveConfirm: 'Dit opnameverzoek goedkeuren?',
    rejectPrompt: 'Reden van afwijzing:',
    markPaidPrompt: 'Externe uitbetalingsreferentie (verplicht — TON tx-hash, bankref., etc.):',
    markPaidRequired: 'externalRef is verplicht om een handmatige uitbetaling te bevestigen.',
    markFailedPrompt: 'Foutmelding:',
    approveError: 'Fout bij goedkeuren opname',
    rejectError: 'Fout bij afwijzen opname',
    updateError: 'Fout bij bijwerken',
    failedLoad: 'Opnames laden mislukt',
    notAuthenticated: 'Niet geauthenticeerd',
    operationFailed: 'Operatie mislukt',
    loading: 'Laden...',
  },
  redirectResolve: {
    missingSlug: 'Slug ontbreekt',
    invalidLink: 'Ongeldige link',
    notFound: 'Link niet gevonden',
    notFoundTitle: 'Link niet gevonden',
    redirecting: 'Doorverwijzen...',
    redirectTitle: 'Doorverwijzing',
  },
  errorBoundary: {
    title: 'Er is een fout opgetreden',
    subtitle: 'Sorry, er ging iets mis. Probeer het opnieuw.',
    goHome: 'Terug naar home',
    reload: 'Pagina herladen',
    devDetails: 'Foutdetails (alleen dev):',
  },
};

export const settingsApp = {
  eyebrow: 'INSTELLINGEN',
  profile: 'Profiel',
  email: 'E-mail',
  telegramId: 'Telegram-ID',
  notLinked: 'Niet gekoppeld',
  linking: 'Koppelen...',
  linkTelegram: 'Mijn Telegram-account koppelen',
  openFromTelegram: '⚠️ Open deze pagina vanuit Telegram om je account te koppelen',
  displayName: 'Weergavenaam',
  displayNameHint: 'Weergavenaam wordt ingesteld bij het aanmaken van het account',
  withdrawal: 'Opname',
  tonAddress: 'TON-bestemmingsadres',
  tonAddressPlaceholder: 'EQAbc123... of 0:...',
  tonAddressHint: 'Dit adres wordt standaard gebruikt voor je opnames',
  tonUpdated: 'TON-adres succesvol bijgewerkt',
  about: 'Over',
  commission: 'Platformcommissie:',
  commissionValue: '3% op elke betaling',
  minWithdrawal: 'Minimumopname:',
  minWithdrawalValue: '100 Stars',
  starsCurrency: 'Stars-valuta:',
  starsCurrencyValue: 'XTR',
  telegramWebAppNotAvailable: 'Telegram WebApp niet beschikbaar. Open deze pagina vanuit Telegram.',
  telegramUserDataNotAvailable: 'Telegram-gebruikersgegevens niet beschikbaar.',
  telegramLinked: 'Telegram-account succesvol gekoppeld!',
  linkTelegramError: 'Fout bij koppelen Telegram-account',
  updateError: 'Fout bij bijwerken',
  mustBeLoggedIn: 'Je moet ingelogd zijn',
};
